// ============================================================================
// BookingRegisterReportScreen — bookings on a date basis (report layout)
// ============================================================================
// One row per booking. The period runs on the chosen DATE BASIS:
//   • Booked on   — created_at: the business written in the period (default, MTD)
//   • Check-in    — arrivals in the period
//   • Check-out   — departures in the period
//   • In house on — one date: the stay covers that night (check-in on or before,
//                   check-out after; a guest leaving that day is not in house).
//                   Cancelled and no-show bookings never stayed and are left out.
// Report layout (Fairbooks pattern): report band with the scope and the counts,
// grid, totals footer; Excel / PDF are built by the API from the same filters.
// Further filters: source, agent, corporate, OTA, taken by, status, advance short.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useBookingSources } from 'hooks/api/useBookingSources';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import DateRangePresetSelector from 'components/common/DateRangePresetSelector';
import { useDateRangePreset } from 'hooks/useDateRangePreset';
import { useReportList, useReportSummary } from 'hooks/api/useReport';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import { agentService } from 'api/services/agentService';
import { corporateService } from 'api/services/corporateService';
import { otaService } from 'api/services/otaService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type {
  BookingRegisterDateBasis,
  BookingRegisterRequest,
  BookingRegisterRow,
  BookingRegisterSummary,
} from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const BASIS_OPTIONS: { value: BookingRegisterDateBasis; label: string; hint: string }[] = [
  { value: 'BookingDate',  label: 'Booked on',   hint: 'Bookings taken in the period.' },
  { value: 'CheckInDate',  label: 'Check-in',    hint: 'Arrivals due in the period.' },
  { value: 'CheckOutDate', label: 'Check-out',   hint: 'Departures due in the period.' },
  { value: 'InHouse',      label: 'In house on', hint: 'Staying that night: check-in on or before the date, check-out after it. Cancelled and no-show bookings are left out.' },
];
const basisLabel = (b: BookingRegisterDateBasis) => BASIS_OPTIONS.find((o) => o.value === b)?.label ?? 'Booked on';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'TENTATIVE',        label: 'Tentative' },
  { value: 'CONFIRMED',        label: 'Confirmed' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'BLOCKED',          label: 'Blocked' },
  { value: 'CHECKED_IN',       label: 'Checked-In' },
  { value: 'CHECKED_OUT',      label: 'Checked-Out' },
  { value: 'CANCELLED',        label: 'Cancelled' },
  { value: 'NO_SHOW',          label: 'No-Show' },
];

const right = (v: React.ReactNode, danger = false) => (
  <span style={{ display: 'block', textAlign: 'right', color: danger ? 'var(--danger, #dc2626)' : undefined }}>{v}</span>
);

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem' };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

const BookingRegisterReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  // Enum-driven source list; 'Any source' is a filter affordance, not a source.
  const { options: enumSourceOptions } = useBookingSources();
  const sourceOptions: SelectOption[] = useMemo(
    () => [{ value: '', label: 'Any source' }, ...enumSourceOptions],
    [enumSourceOptions],
  );

  // ── Scope ─────────────────────────────────────────────────────────────────
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  React.useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  // ── Date basis + period ───────────────────────────────────────────────────
  // A range for booked on / check-in / check-out; one date for in house.
  const [dateBasis, setDateBasis] = useState<BookingRegisterDateBasis>('BookingDate');
  const dateRange = useDateRangePreset('MTD');
  const [inHouseOn, setInHouseOn] = useState(todayIso());
  const isInHouse = dateBasis === 'InHouse';
  const from = isInHouse ? inHouseOn : dateRange.value.from;
  const to = isInHouse ? inHouseOn : dateRange.value.to;
  const changeBasis = useCallback((b: BookingRegisterDateBasis) => {
    setDateBasis(b);
    // Arrivals / departures are usually looked at for today; the booked-on window keeps its month.
    if (b === 'BookingDate') dateRange.setPreset('MTD');
    else if (b !== 'InHouse') dateRange.setPreset('TODAY');
  }, [dateRange]);

  // ── Refinements ───────────────────────────────────────────────────────────
  const [bookingSource, setBookingSource] = useState('');
  const [agentId, setAgentId] = useState('');
  const [corporateId, setCorporateId] = useState('');
  const [otaId, setOtaId] = useState('');
  const [bookingTakenByName, setBookingTakenByName] = useState('');
  const [bookingStatusCodes, setBookingStatusCodes] = useState<string[]>([]);
  const [advanceShortOnly, setAdvanceShortOnly] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const enabled = !!propertyId && !!from && !!to && from <= to;

  const filter: BookingRegisterRequest = useMemo(() => ({
    propertyId, from, to, dateBasis,
    bookingSource:      bookingSource || undefined,
    agentId:            agentId || undefined,
    corporateId:        corporateId || undefined,
    otaId:              otaId || undefined,
    bookingTakenByName: bookingTakenByName.trim() || undefined,
    bookingStatusCodes: bookingStatusCodes.length ? bookingStatusCodes : undefined,
    advanceShortOnly:   advanceShortOnly || undefined,
  }), [propertyId, from, to, dateBasis, bookingSource, agentId, corporateId, otaId,
       bookingTakenByName, bookingStatusCodes, advanceShortOnly]);
  React.useEffect(() => { setPage(1); }, [filter]);
  const listRequest: BookingRegisterRequest = useMemo(() => ({ ...filter, page, pageSize }), [filter, page, pageSize]);

  const { data: rawData, pagination, loading, error, refetch } = useReportList<BookingRegisterRow>(
    (req) => reportService.getBookingRegister(req as BookingRegisterRequest), listRequest, enabled);
  const data = useMemo(() => rawData.map((r) => ({ ...r, id: r.bookingId as unknown as string })), [rawData]);
  const { data: summary, refetch: refetchSummary } = useReportSummary<BookingRegisterSummary>(
    (f) => reportService.getBookingRegisterSummary(f as BookingRegisterRequest), filter, enabled);

  // Refinements beyond the scope (property + period) — counted on the filter badge and named in the band.
  const refinements = useMemo(() => {
    const r: string[] = [];
    if (bookingSource) r.push(bookingSource);
    if (agentId) r.push('agent');
    if (corporateId) r.push('corporate');
    if (otaId) r.push('OTA');
    if (bookingTakenByName.trim()) r.push(`taken by "${bookingTakenByName.trim()}"`);
    if (bookingStatusCodes.length) r.push(bookingStatusCodes.map((c) => STATUS_OPTIONS.find((o) => o.value === c)?.label ?? c).join(', '));
    if (advanceShortOnly) r.push('advance short');
    return r;
  }, [bookingSource, agentId, corporateId, otaId, bookingTakenByName, bookingStatusCodes, advanceShortOnly]);
  const appliedCount = refinements.length + (dateBasis !== 'BookingDate' || dateRange.value.preset !== 'MTD' ? 1 : 0);

  // ── Option loaders ────────────────────────────────────────────────────────
  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);
  const loadAgents = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await agentService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 200, includeInactive: false } as any);
    return (res.data ?? []).map((a: any) => ({ value: a.id, label: `${a.agentCode} - ${a.agentName}` }));
  }, []);
  const loadCorporates = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await corporateService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 200, includeInactive: false } as any);
    return (res.data ?? []).map((c: any) => ({ value: c.id, label: `${c.corporateCode} - ${c.companyName}` }));
  }, []);
  const loadOtas = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await otaService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 200, includeInactive: false } as any);
    return (res.data ?? []).map((o: any) => ({ value: o.id, label: `${o.otaCode} - ${o.otaName}` }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setDateBasis('BookingDate');
    dateRange.setPreset('MTD');
    setInHouseOn(todayIso());
    setBookingSource('');
    setAgentId('');
    setCorporateId('');
    setOtaId('');
    setBookingTakenByName('');
    setBookingStatusCodes([]);
    setAdvanceShortOnly(false);
    // Property is kept — every report needs a property scope.
  }, [dateRange]);

  // ── Export / Print — formatted files built by the API (the Fairbooks way) ──
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await reportService.exportBookingRegister(filter, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `BookingRegister_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, filter, from, to]);
  const handleExport = useCallback(() => { void exportFile('xlsx'); }, [exportFile]);
  const handlePrint = useCallback(() => { void exportFile('pdf'); }, [exportFile]);

  // ── Grid ──────────────────────────────────────────────────────────────────
  // Rows come sorted by the server on the date basis (newest bookings first on
  // booked on; by date of stay otherwise) — the grid does not re-sort a page.
  type Row = BookingRegisterRow & { id: string };
  const columns: SyutGridColumn<Row>[] = useMemo(() => [
    { id: 'bookingNumber', header: 'Booking #', accessorKey: 'bookingNumber', size: 130 },
    { id: 'bookingDate', header: 'Booked on', accessorKey: 'bookingDate', size: 105,
      cell: ({ row }) => (
        <span>{formatDate(row.original.bookingDate)}
          {row.original.bookingTakenByName && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.original.bookingTakenByName}</div>}
        </span>
      ) },
    { id: 'guestName', header: 'Guest', accessorKey: 'guestName', size: 170,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          {row.original.guestPhone && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.original.guestPhone}</div>}
        </span>
      ) },
    { id: 'sourceParty', header: 'Source / Party', accessorKey: 'sourceParty', size: 180,
      cell: ({ row }) => (
        <span>{row.original.sourceParty || '—'}
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.original.bookingSource}</div>
        </span>
      ) },
    { id: 'checkInDate', header: 'Check-in', accessorKey: 'checkInDate', size: 105, cell: ({ getValue }) => formatDate(getValue() as string) },
    { id: 'checkOutDate', header: 'Check-out', accessorKey: 'checkOutDate', size: 105, cell: ({ getValue }) => formatDate(getValue() as string) },
    { id: 'numNights', header: 'Nights', accessorKey: 'numNights', size: 65, cell: ({ getValue }) => right(getValue() as number) },
    { id: 'totalRooms', header: 'Rooms', accessorKey: 'totalRooms', size: 65, cell: ({ getValue }) => right(getValue() as number) },
    { id: 'pax', header: 'Pax', accessorKey: 'numAdults', size: 65,
      cell: ({ row }) => right(row.original.numChildren > 0 ? `${row.original.numAdults}+${row.original.numChildren}` : row.original.numAdults) },
    { id: 'ratePerRoom', header: 'Rate', accessorKey: 'ratePerRoom', size: 105, cell: ({ getValue }) => right(formatCurrency(getValue() as number)) },
    { id: 'grossAmount', header: 'Gross', accessorKey: 'grossAmount', size: 120, cell: ({ getValue }) => right(formatCurrency(getValue() as number)) },
    { id: 'discountAmount', header: 'Discount', accessorKey: 'discountAmount', size: 105,
      cell: ({ getValue }) => right((getValue() as number) ? formatCurrency(getValue() as number) : '—') },
    { id: 'netAmount', header: 'Net', accessorKey: 'netAmount', size: 120, cell: ({ getValue }) => right(<b>{formatCurrency(getValue() as number)}</b>) },
    { id: 'advance', header: 'Advance', accessorKey: 'advanceReceived', size: 140,
      cell: ({ row }) => {
        const { advanceReceived: got, advanceRequired: req } = row.original;
        const short = req > 0 && got < req;
        return right(
          <span title={req > 0 ? `Required ${formatCurrency(req)}` : 'No advance required'}>
            {formatCurrency(got)}
            {req > 0 && <div style={{ fontSize: '0.72rem', color: short ? 'var(--danger, #dc2626)' : 'var(--text-muted)' }}>of {formatCurrency(req)}</div>}
          </span>,
        );
      } },
    { id: 'balanceDue', header: 'Balance', accessorKey: 'balanceDue', size: 115,
      cell: ({ getValue }) => right(formatCurrency(getValue() as number), (getValue() as number) < 0) },
    { id: 'bookingStatusName', header: 'Status', accessorKey: 'bookingStatusName', size: 115 },
  ], []);

  // ── Header actions ────────────────────────────────────────────────────────
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { refetch(); refetchSummary(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: 'Export to Excel', onClick: handleExport, variant: 'icon' });
      buttons.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: 'Print booking register', onClick: handlePrint, variant: 'icon' });
    }
    return buttons;
  }, [refetch, refetchSummary, handleExport, handlePrint, exporting, printing, allowedActions]);

  // ── Report band ───────────────────────────────────────────────────────────
  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: basisLabel(dateBasis), value: periodLabel, primary: true, icon: 'calendar',
      title: BASIS_OPTIONS.find((o) => o.value === dateBasis)?.hint },
    ...(refinements.length ? [{ label: 'Filtered', value: refinements.join(' · '), icon: 'filter' as const }] : []),
    ...(enabled && summary ? [
      { label: 'Bookings', value: `${summary.totalBookings}  (${summary.totalRooms} rooms)` },
      { label: 'Room nights', value: String(summary.totalRoomNights), icon: 'clock' as const },
      { label: 'Pax', value: summary.totalChildren > 0 ? `${summary.totalAdults} + ${summary.totalChildren} ch` : String(summary.totalAdults), icon: 'users' as const },
      { label: 'Net', value: formatCurrency(summary.totalNetAmount), title: 'Gross (with rate adjustments) less discount' },
      { label: 'Balance due', value: formatCurrency(summary.totalBalanceDue) },
    ] : []),
  ];

  const footer = enabled && summary && summary.totalBookings > 0 ? (
    <>
      <b>Totals</b>
      <span>Gross <b>{formatCurrency(summary.totalGrossAmount)}</b></span>
      <span>Discount <b>{formatCurrency(summary.totalDiscount)}</b></span>
      <span>Net <b>{formatCurrency(summary.totalNetAmount)}</b></span>
      <span>Advance <b>{formatCurrency(summary.totalAdvanceReceived)}</b>
        <span style={{ color: 'var(--text-muted)' }}> of {formatCurrency(summary.totalAdvanceRequired)}</span></span>
      <span style={{ marginLeft: 'auto' }}>Balance due <b>{formatCurrency(summary.totalBalanceDue)}</b></span>
    </>
  ) : undefined;

  const basisHint = BASIS_OPTIONS.find((o) => o.value === dateBasis)?.hint;

  return (
    <EnterpriseScreenShell
      title="Booking Register"
      description="Bookings by booking date, arrival, departure, or in house on a date — with rate, pax, discount, advance and status."
      icon={<IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Date basis"
                  value={dateBasis}
                  onChange={(v) => changeBasis(((v as string) || 'BookingDate') as BookingRegisterDateBasis)}
                  options={BASIS_OPTIONS.map(({ value, label }) => ({ value, label }))}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
                {basisHint && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{basisHint}</div>}
              </div>

              {isInHouse ? (
                <div className="filter-section">
                  <label className="form-label" style={labelStyle}>In house on <span className="text-danger">*</span></label>
                  <input type="date" className="form-input" value={inHouseOn} onChange={(e) => setInHouseOn(e.target.value)} style={inputStyle} />
                </div>
              ) : (
                <div className="filter-section">
                  <label className="form-label" style={labelStyle}>{basisLabel(dateBasis)} <span className="text-danger">*</span></label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <DateRangePresetSelector
                      preset={dateRange.value.preset}
                      from={dateRange.value.from}
                      to={dateRange.value.to}
                      onPresetChange={dateRange.setPreset}
                      onFromChange={dateRange.setCustomFrom}
                      onToChange={dateRange.setCustomTo}
                    />
                  </div>
                </div>
              )}

              <div className="filter-section">
                <SyutSelect label="Booking source" value={bookingSource} onChange={(v) => setBookingSource((v as string) || '')}
                  options={sourceOptions} isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Agent" value={agentId} onChange={(v) => setAgentId((v as string) || '')}
                  isAsync loadOptions={loadAgents} defaultOptions={true} cacheOptions={false} isClearable menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Corporate" value={corporateId} onChange={(v) => setCorporateId((v as string) || '')}
                  isAsync loadOptions={loadCorporates} defaultOptions={true} cacheOptions={false} isClearable menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="OTA" value={otaId} onChange={(v) => setOtaId((v as string) || '')}
                  isAsync loadOptions={loadOtas} defaultOptions={true} cacheOptions={false} isClearable menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={labelStyle}>Booking taken by</label>
                <input type="text" className="form-input" value={bookingTakenByName} onChange={(e) => setBookingTakenByName(e.target.value)}
                  placeholder="Part of the name…" style={inputStyle} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Booking status"
                  value={bookingStatusCodes}
                  onChange={(v) => setBookingStatusCodes(Array.isArray(v) ? (v as string[]) : (v ? [v as string] : []))}
                  options={isInHouse ? STATUS_OPTIONS.filter((o) => o.value !== 'CANCELLED' && o.value !== 'NO_SHOW') : STATUS_OPTIONS}
                  isMulti
                  isClearable
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={advanceShortOnly} onChange={(e) => setAdvanceShortOnly(e.target.checked)} />
                  Advance short only
                </label>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  Bookings owing a deposit (advance required &gt; received).
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
            Pick a property and a period in the filters.
          </div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error.message}</div>
        ) : (
          <SyutGrid
            data={data}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={pagination?.totalItems ?? 0}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(i) => setPage(i + 1)}
            onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            isLoading={loading}
            emptyMessage={isInHouse ? 'No guests in house on that date.' : 'No bookings match the filters.'}
          />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default BookingRegisterReportScreen;
